import { useEffect, useMemo, useRef, useState } from "react";
import { api, BACKEND_URL, buildKickLoginUrl, buildLoginUrl } from "../lib/api";
import {
  Camera,
  Check,
  Link2,
  MessageSquare,
  Palette,
  Rabbit,
  ShieldCheck,
  Sparkles,
  Star,
  Upload,
  UserRound,
} from "lucide-react";
import { CurrencyIcon, currencyAmountLabel, currencyLabel, sortCurrencies } from "../lib/currencyDisplay";
import { viewerRequestStatusLabel, viewerRequestStatusTone } from "../lib/viewerRequestStatus";
import PublicSiteLayout, { PublicLinkButton, PUBLIC_LINKS } from "../components/PublicSiteLayout";
import ProfileBadgeStrip from "../components/ProfileBadgeStrip";

const FALLBACK_ACCENT_COLORS = [
  { id: "ember", name: "Ember", value: "#E85D04" },
  { id: "gold", name: "Gold", value: "#F6AE2D" },
  { id: "clover", name: "Clover", value: "#2FBF71" },
  { id: "mint", name: "Mint", value: "#35C2A5" },
  { id: "sky", name: "Sky", value: "#3A86FF" },
  { id: "cobalt", name: "Cobalt", value: "#5E7CE2" },
  { id: "violet", name: "Violet", value: "#8B5CF6" },
  { id: "magenta", name: "Magenta", value: "#C056A1" },
  { id: "rose", name: "Rose", value: "#E05263" },
  { id: "crimson", name: "Crimson", value: "#D7263D" },
];

export default function Profile() {
  const [me, setMe] = useState({ authenticated: false });
  const [currencies, setCurrencies] = useState([]);
  const [streamer, setStreamer] = useState(null);
  const [balances, setBalances] = useState({});
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [balancesError, setBalancesError] = useState("");
  const [currencyHistory, setCurrencyHistory] = useState([]);
  const [redeemHistory, setRedeemHistory] = useState([]);
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [{ data: m }, { data: meta }, { data: authProfile }] = await Promise.all([
        api.get("/viewer/me"),
        api.get("/interactions"),
        api.get("/auth/me"),
      ]);
      const merged = mergeAuthProfile(m, authProfile);
      setMe(merged);
      setCurrencies(meta.currencies || []);
      setStreamer(meta.streamer);
      if (merged.authenticated) {
        setBalancesLoading(true);
        setBalancesError("");
        try {
          const viewerId = merged.viewer?.twitch_user_id || merged.viewer?.platform_user_id;
          if (!viewerId) {
            setBalances({});
            setBalancesError("Could not load balances.");
          } else {
            const { data: balanceData } = await api.get(`/currency/viewers/${encodeURIComponent(viewerId)}/balances`, {
              params: {
                login: merged.viewer?.login || merged.viewer?.display_name || viewerId,
              },
            });
            setBalances(balanceData.balances || {});
          }
        } catch (e) {
          setBalances({});
          setBalancesError("Could not load balances.");
        } finally {
          setBalancesLoading(false);
        }

        setActivityLoading(true);
        setActivityError("");
        const [currencyHistoryResult, redeemResult, accountsResult] = await Promise.allSettled([
          api.get("/auth/currency/history", { params: { limit: 8 } }),
          api.get("/auth/interaction-requests", { params: { limit: 8 } }),
          api.get("/auth/connected-accounts"),
        ]);
        setCurrencyHistory(currencyHistoryResult.status === "fulfilled" ? currencyHistoryResult.value.data?.entries || [] : []);
        setRedeemHistory(redeemResult.status === "fulfilled" ? redeemResult.value.data?.requests || [] : []);
        setConnectedAccounts(accountsResult.status === "fulfilled" ? accountsResult.value.data?.accounts || [] : []);
        if ([currencyHistoryResult, redeemResult].some((result) => result.status === "rejected")) {
          setActivityError("Some activity could not be loaded right now.");
        }
        setActivityLoading(false);
      } else {
        setBalances({});
        setBalancesError("");
        setBalancesLoading(false);
        setCurrencyHistory([]);
        setRedeemHistory([]);
        setConnectedAccounts([]);
        setActivityError("");
        setActivityLoading(false);
      }
    } catch (e) {
      // Keep the signed-out shell available if profile services are unavailable.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const profileCurrencies = useMemo(
    () => sortCurrencies(currencies.filter((currency) => (
      currency.visible !== false ||
      currency.id === "giveaway_currency" ||
      currency.id === "event_currency" ||
      currency.enabled === false
    ))),
    [currencies]
  );

  const handleProfileSaved = (profile) => {
    setMe((current) => mergeAuthProfile(current, profile));
    window.dispatchEvent(new CustomEvent("viewer-profile-updated", { detail: profile }));
  };

  if (loading) {
    return (
      <PublicSiteLayout>
        <div className="p-10 text-sm text-[hsl(var(--muted-foreground))]">Loading profile...</div>
      </PublicSiteLayout>
    );
  }

  return (
    <PublicSiteLayout>
      <div className="profile-page w-full max-w-6xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-8">
          <div>
            <div className="label-eyebrow text-[hsl(var(--currency-event))]">Viewer profile</div>
            <h1 className="profile-page-title mt-2">Your profile</h1>
            <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))] max-w-2xl">
              {streamer ? `Connected to ${streamer.display_name}'s community systems.` : "Your community home for identity, balances, and recent activity."}
            </p>
          </div>
        </div>

        {!me.authenticated ? (
          <SignedOut />
        ) : (
          <SignedIn
            me={me}
            currencies={profileCurrencies}
            balances={balances}
            balancesLoading={balancesLoading}
            balancesError={balancesError}
            currencyHistory={currencyHistory}
            redeemHistory={redeemHistory}
            connectedAccounts={connectedAccounts}
            activityLoading={activityLoading}
            activityError={activityError}
            onProfileSaved={handleProfileSaved}
          />
        )}
      </div>
    </PublicSiteLayout>
  );
}

function SignedOut() {
  return (
    <div className="surface p-8 sm:p-12 text-center" data-testid="profile-signed-out">
      <div className="inline-flex w-14 h-14 rounded bg-[hsl(var(--primary))]/15 items-center justify-center mb-5">
        <ShieldCheck className="w-7 h-7 text-[hsl(var(--primary))]" />
      </div>
      <div className="label-eyebrow">Your profile</div>
      <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">Sign in with Twitch or Kick</h2>
      <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))] max-w-md mx-auto">
        Sign in to manage your viewer identity, balances, badges, and community activity.
      </p>
      <div className="mt-7 flex flex-wrap justify-center gap-3">
        <a href={buildLoginUrl("viewer", "/profile")} target="_top" rel="noopener" data-testid="profile-login-btn" className="inline-flex items-center gap-2 px-5 py-3 rounded bg-[#9146ff] text-white font-medium hover:brightness-110 transition">
          <Rabbit className="w-4 h-4" /> Sign in with Twitch
        </a>
        <a href={buildKickLoginUrl("viewer", "/profile")} target="_top" rel="noopener" data-testid="profile-kick-login-btn" className="inline-flex items-center gap-2 px-5 py-3 rounded border border-[#53fc18]/70 bg-[#53fc18]/15 text-[#d8ffd1] font-medium hover:bg-[#53fc18]/25 transition">
          <Link2 className="w-4 h-4" /> Sign in with Kick
        </a>
        <PublicLinkButton href="/redeems"><Sparkles className="w-4 h-4" /> View redeems</PublicLinkButton>
      </div>
    </div>
  );
}

function SignedIn({ me, currencies, balances, balancesLoading, balancesError, currencyHistory, redeemHistory, connectedAccounts, activityLoading, activityError, onProfileSaved }) {
  const v = me.viewer;
  return (
    <div className="space-y-6" data-testid="profile-signed-in">
      <IdentityHub viewer={v} connectedAccounts={connectedAccounts} onProfileSaved={onProfileSaved} />
      <CurrencyBalancesSection
        currencies={currencies}
        balances={balances}
        loading={balancesLoading}
        error={balancesError}
      />
      <CompactActivity
        currencies={currencies}
        currencyHistory={currencyHistory}
        redeemHistory={redeemHistory}
        loading={activityLoading}
        error={activityError}
      />
    </div>
  );
}

function IdentityHub({ viewer, connectedAccounts, onProfileSaved }) {
  const accent = viewer.accent_color || FALLBACK_ACCENT_COLORS[6].value;
  const activeTitle = viewer.active_title;
  const featuredBadges = viewer.featured_badges || [];
  return (
    <section className="profile-identity surface" aria-label="Profile identity">
      <div className="profile-identity-layout">
          <div className="profile-identity-summary">
            <ProfileAvatarEditor viewer={viewer} accent={accent} onProfileSaved={onProfileSaved} />
            <div className="flex-1 min-w-0">
              <div className="label-eyebrow" style={{ color: accent }}>Signed in with {platformName(viewer.auth_provider || viewer.primary_platform)}</div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <h2 className="profile-display-name">
                  {viewer.preferred_name || viewer.display_name || viewer.login || "Viewer"}
                </h2>
                <ProfileBadgeStrip badges={featuredBadges} size="lg" />
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {activeTitle && <ProfileTitle title={activeTitle} />}
                {viewer.pronouns && <MiniBadge>{viewer.pronouns}</MiniBadge>}
              </div>
              {viewer.about && (
                <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[hsl(var(--muted-foreground))] whitespace-pre-wrap">
                  {viewer.about}
                </p>
              )}
              <div className="profile-metadata">
                <span>
                  @{viewer.login || "viewer"}
                </span>
                <span>
                  {formatRole(viewer.role || "viewer")}
                </span>
                <span>
                  {viewer.watchtime_minutes || 0}m active watchtime
                </span>
              </div>
            </div>
          </div>

        <ConnectedAccounts accounts={connectedAccounts} viewer={viewer} />
        <ProfileIdentityForm viewer={viewer} onProfileSaved={onProfileSaved} />
      </div>
    </section>
  );
}

function ProfileAvatarEditor({ viewer, accent, onProfileSaved }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const name = viewer.preferred_name || viewer.display_name || viewer.login || "Viewer";
  const avatarUrl = resolveMediaUrl(viewer.profile_image_url);

  const uploadAvatar = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setMessage("");
    setError("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/auth/profile/avatar", formData);
      onProfileSaved?.(data);
      setMessage("Avatar saved.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not upload avatar.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="profile-avatar-editor">
      <div className="profile-avatar-frame">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt=""
            className="profile-avatar-image object-cover"
            style={{ borderColor: accent }}
          />
        ) : (
          <div
            className="profile-avatar-image bg-black/50 flex items-center justify-center"
            style={{ borderColor: accent }}
          >
            <span className="text-4xl sm:text-5xl font-black text-[hsl(var(--foreground))]">{initialsForName(name)}</span>
          </div>
        )}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="profile-avatar-upload"
          aria-label="Upload avatar"
          title="Upload avatar"
        >
          {uploading ? <Upload className="w-4 h-4 animate-pulse" /> : <Camera className="w-4 h-4" />}
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadAvatar} />
      <p className="mt-4 text-xs leading-relaxed text-[hsl(var(--muted-foreground))]">
        Avatar must be safe for work. NSFW content may result in temporary bans.
      </p>
      {message && <div className="mt-2 text-xs text-emerald-300">{message}</div>}
      {error && <div className="mt-2 text-xs text-[hsl(var(--destructive))]">{error}</div>}
    </div>
  );
}

function ProfileIdentityForm({ viewer, onProfileSaved }) {
  const awards = viewer.awards || {};
  const unlockedBadges = (awards.badges || []).filter((badge) => badge.unlocked);
  const unlockedTitles = (awards.titles || []).filter((title) => title.unlocked);
  const colorOptions = viewer.profile_options?.accent_colors?.length ? viewer.profile_options.accent_colors : FALLBACK_ACCENT_COLORS;
  const [preferredName, setPreferredName] = useState(viewer.preferred_name || "");
  const [pronouns, setPronouns] = useState(normalizePronouns(viewer.pronouns));
  const [about, setAbout] = useState(viewer.about || "");
  const [accentColor, setAccentColor] = useState(viewer.accent_color || FALLBACK_ACCENT_COLORS[6].value);
  const [avatarSource, setAvatarSource] = useState(viewer.avatar_source || "platform");
  const [activeTitleId, setActiveTitleId] = useState(viewer.active_title_id || "");
  const [featuredBadgeIds, setFeaturedBadgeIds] = useState(() => {
    if (Array.isArray(viewer.featured_badge_ids) && viewer.featured_badge_ids.length) return viewer.featured_badge_ids.slice(0, 3);
    return (viewer.featured_badges || []).map((badge) => badge.id).filter(Boolean).slice(0, 3);
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setPreferredName(viewer.preferred_name || "");
    setPronouns(normalizePronouns(viewer.pronouns));
    setAbout(viewer.about || "");
    setAccentColor(viewer.accent_color || FALLBACK_ACCENT_COLORS[6].value);
    setAvatarSource(viewer.avatar_source || "platform");
    setActiveTitleId(viewer.active_title_id || "");
    if (Array.isArray(viewer.featured_badge_ids) && viewer.featured_badge_ids.length) {
      setFeaturedBadgeIds(viewer.featured_badge_ids.slice(0, 3));
    } else {
      setFeaturedBadgeIds((viewer.featured_badges || []).map((badge) => badge.id).filter(Boolean).slice(0, 3));
    }
  }, [viewer]);

  const toggleBadge = (badgeId) => {
    setFeaturedBadgeIds((current) => {
      if (current.includes(badgeId)) return current.filter((id) => id !== badgeId);
      if (current.length >= 3) return current;
      return [...current, badgeId];
    });
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage("");
    setError("");
    try {
      const { data } = await api.patch("/auth/profile", {
        preferred_name: preferredName,
        pronouns,
        about,
        accent_color: accentColor,
        avatar_source: avatarSource,
        active_title_id: activeTitleId,
        featured_badge_ids: featuredBadgeIds,
      });
      onProfileSaved?.(data);
      setMessage("Profile saved.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not save profile.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="profile-form">
      <fieldset className="space-y-4 min-w-0">
        <legend className="profile-fieldset-title">Your details</legend>
        <ProfileTextField
          label="Chat name"
          value={preferredName}
          onChange={setPreferredName}
          placeholder={viewer.display_name || viewer.login || "Not set"}
          maxLength={12}
          disabled={saving}
        />
        <PronounsSelect value={pronouns} onChange={setPronouns} disabled={saving} />
        <AboutField value={about} onChange={setAbout} disabled={saving} />
      </fieldset>

      <fieldset className="space-y-4 min-w-0">
        <legend className="profile-fieldset-title">Make it yours</legend>
        <ColorPicker colors={colorOptions} value={accentColor} onChange={setAccentColor} disabled={saving} />
        <AvatarSourcePicker viewer={viewer} value={avatarSource} onChange={setAvatarSource} disabled={saving} />
        <TitlePicker titles={unlockedTitles} value={activeTitleId} onChange={setActiveTitleId} disabled={saving} />
        <BadgePicker badges={unlockedBadges} selected={featuredBadgeIds} onToggle={toggleBadge} disabled={saving} />
      </fieldset>
        <div className="profile-save-row">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex justify-center items-center gap-2 px-4 py-2 rounded bg-[hsl(var(--primary))] text-white text-sm font-medium hover:brightness-110 transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? <Upload className="w-4 h-4 animate-pulse" /> : <Check className="w-4 h-4" />}
            {saving ? "Saving..." : "Save profile"}
          </button>
          {message && <div role="status" className="text-sm text-emerald-300">{message}</div>}
          {error && <div role="alert" className="text-sm text-[hsl(var(--destructive))]">{error}</div>}
        </div>
    </form>
  );
}

function ConnectedAccounts({ accounts, viewer }) {
  const linked = new Set((accounts || []).map((account) => account.platform));
  const primaryPlatform = viewer?.primary_platform || (String(viewer?.twitch_user_id || "").startsWith("kick:") ? "kick" : "twitch");
  const hasTwitch = linked.has("twitch") || primaryPlatform === "twitch";
  const hasKick = linked.has("kick") || primaryPlatform === "kick";
  return (
    <aside className="profile-connections" aria-label="Connected accounts">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">
        <Link2 className="w-4 h-4 text-[hsl(var(--currency-event))]" /> Connected accounts
      </div>
      <div className="mt-4 grid grid-cols-1 gap-2">
        {hasTwitch ? (
          <span className="profile-connection-status">
            <span>Twitch</span><span><Check size={14} aria-hidden="true" /> Linked</span>
          </span>
        ) : (
          <a
            href={buildLoginUrl("viewer", "/profile")}
            target="_top"
            rel="noopener"
            className="inline-flex items-center gap-2 rounded border border-[#9146ff]/70 bg-[#9146ff]/15 px-3 py-2 text-xs text-white hover:bg-[#9146ff]/30 transition"
          >
            <Link2 className="w-3.5 h-3.5" /> Connect Twitch
          </a>
        )}
        {hasKick ? (
          <span className="profile-connection-status">
            <span>Kick</span><span><Check size={14} aria-hidden="true" /> Linked</span>
          </span>
        ) : (
          <a
            href={buildKickLoginUrl("viewer", "/profile")}
            target="_top"
            rel="noopener"
            className="inline-flex items-center gap-2 rounded border border-[hsl(var(--currency-event))] bg-[hsl(var(--currency-event))]/15 px-3 py-2 text-xs text-white hover:bg-[hsl(var(--currency-event))]/30 transition"
          >
            <Link2 className="w-3.5 h-3.5" /> Connect Kick
          </a>
        )}
      </div>
      <div className="mt-5 pt-4 border-t border-white/10">
        <div className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Profile color</div>
        <div className="mt-2 flex items-center gap-2">
          <span className="h-5 w-5 rounded border border-white/30" style={{ background: viewer.accent_color || FALLBACK_ACCENT_COLORS[6].value }} />
          <span className="text-sm text-[hsl(var(--foreground))]">{colorNameForValue(viewer.accent_color, viewer.profile_options?.accent_colors)}</span>
        </div>
      </div>
    </aside>
  );
}

function CurrencyBalancesSection({ currencies, balances, loading, error }) {
  const balanceEntries = Object.entries(balances || {});
  const fallbackCurrencies = balanceEntries
    .filter(([id]) => !currencies.some((currency) => currency.id === id))
    .map(([id]) => ({ id, name: currencyLabel({ id }) }));
  const displayedCurrencies = [...currencies, ...fallbackCurrencies];

  return (
    <section className="profile-section" aria-label="Currency balances">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <div className="label-eyebrow mb-2 text-[hsl(var(--currency-event))]">Currency</div>
          <h2 className="text-2xl font-semibold tracking-tight text-[hsl(var(--foreground))]">Balances</h2>
        </div>
        <PublicLinkButton href="/redeems"><Sparkles className="w-4 h-4" /> Use Redeems</PublicLinkButton>
      </div>

      {loading ? (
        <div className="mt-5 text-sm text-[hsl(var(--muted-foreground))]">Loading balances...</div>
      ) : error ? (
        <div className="mt-5 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-100">Could not load balances.</div>
      ) : displayedCurrencies.length === 0 ? (
        <div className="mt-5 rounded border border-dashed border-[hsl(var(--border))] bg-black/40 px-4 py-5 text-sm text-[hsl(var(--muted-foreground))]">
          <EmptyState
            title="No balances yet."
            body="Balances will appear here after currency is granted, earned, charged, or refunded."
            actions={<PublicLinkButton href="/redeems"><Sparkles className="w-4 h-4" /> Browse redeems</PublicLinkButton>}
          />
        </div>
      ) : (
        <div className="profile-balances mt-5">
          {displayedCurrencies.map((currency) => {
            const balance = balances?.[currency.id] ?? 0;
            const currencyInactive = currency.enabled === false;
            const inactiveMessage = currency.id === "event_currency"
              ? "Event Tokens are disabled right now."
              : "This currency is disabled right now.";
            return (
              <div key={currency.id} className={`profile-balance${currencyInactive ? " is-inactive" : ""}`}>
                <div className="flex items-center gap-2">
                  <CurrencyIcon currency={currency} className="w-4 h-4" />
                  <div className="label-eyebrow" style={{ color: currency.color }}>{currencyLabel(currency)}</div>
                </div>
                <div className="profile-balance-amount">
                  {Number(balance || 0).toLocaleString()}
                </div>
                <p className="mt-3 text-xs leading-relaxed text-[hsl(var(--muted-foreground))]">
                  {currencyInactive ? inactiveMessage : currencyDescription(currency)}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function CompactActivity({ currencies, currencyHistory, redeemHistory, loading, error }) {
  const currencyMap = useMemo(() => {
    const map = {};
    currencies.forEach((currency) => { map[currency.id] = currency; });
    return map;
  }, [currencies]);

  const items = useMemo(() => {
    const currencyItems = (currencyHistory || []).map((entry) => ({
      id: `currency-${entry.id || entry.ledger_id || entry.timestamp}`,
      title: currencySourceLabel(entry.source),
      body: currencyAmountLabel(entry.amount, currencyMap[entry.currency_id] || { id: entry.currency_id, name: entry.currency_name }),
      time: entry.timestamp,
      positive: Number(entry.amount || 0) > 0,
      status: "",
    }));
    const redeemItems = (redeemHistory || []).map((request) => ({
      id: `redeem-${request.request_id || request.id || request.created_at}`,
      title: request.interaction_name || "Redeem request",
      body: "Redeem",
      time: request.created_at,
      positive: false,
      status: request.status,
    }));
    return [...currencyItems, ...redeemItems]
      .sort((a, b) => new Date(b.time || 0).getTime() - new Date(a.time || 0).getTime())
      .slice(0, 6);
  }, [currencyHistory, redeemHistory, currencyMap]);

  return (
    <section id="activity" className="profile-section scroll-mt-24">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="label-eyebrow mb-2 text-[hsl(var(--currency-event))]">Recent activity</div>
          <h2 className="text-2xl font-semibold tracking-tight text-[hsl(var(--foreground))]">Latest movement</h2>
        </div>
        <PublicLinkButton href={PUBLIC_LINKS.discord}><MessageSquare className="w-4 h-4" /> Community</PublicLinkButton>
      </div>
      {error && (
        <div className="mt-4 rounded border border-amber-400/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
          {error}
        </div>
      )}
      {loading ? (
        <div className="mt-5 text-sm text-[hsl(var(--muted-foreground))]">Loading activity...</div>
      ) : items.length === 0 ? (
        <div className="mt-5 rounded border border-dashed border-[hsl(var(--border))] bg-black/40 px-4 py-5 text-sm text-[hsl(var(--muted-foreground))]">
          <EmptyState
            title="No activity yet."
            body="Currency changes and redeems will appear here once they happen."
            actions={<PublicLinkButton href="/redeems" primary><Sparkles className="w-4 h-4" /> Go to Redeems</PublicLinkButton>}
          />
        </div>
      ) : (
        <div className="profile-activity-list mt-5">
          {items.map((item) => (
            <ActivityRow key={item.id} item={item} />
          ))}
        </div>
      )}
    </section>
  );
}

function ActivityRow({ item }) {
  return (
    <div className="profile-activity-row">
      <div className="min-w-0">
        <div className="font-semibold text-[hsl(var(--foreground))] break-words">{item.title}</div>
        <div className={`mt-1 text-sm ${item.positive ? "text-emerald-300" : "text-[hsl(var(--muted-foreground))]"}`}>{item.body}</div>
      </div>
      <div className="flex flex-col items-start sm:items-end gap-2">
        {item.status ? <StatusBadge status={item.status} /> : null}
        <div className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] sm:text-right">{formatDate(item.time)}</div>
      </div>
    </div>
  );
}

function EmptyState({ title, body, actions }) {
  return (
    <div>
      <div className="font-semibold text-[hsl(var(--foreground))]">{title}</div>
      <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">{body}</p>
      {actions && <div className="mt-4 flex flex-wrap gap-3">{actions}</div>}
    </div>
  );
}

function ProfileTextField({ label = "", value, onChange, placeholder, disabled, maxLength = 32 } = {}) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">{label}</span>
      <input
        value={value}
        maxLength={maxLength}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className="mt-2 w-full rounded border border-[hsl(var(--border))] bg-black/40 px-3 py-2 text-sm text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] disabled:opacity-70"
      />
      <span className="mt-1 block text-[11px] text-[hsl(var(--muted-foreground))]">{value.length}/{maxLength}</span>
    </label>
  );
}

function AboutField({ value, onChange, disabled }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">About</span>
      <textarea
        value={value}
        maxLength={300}
        rows={4}
        disabled={disabled}
        placeholder="A short note for your profile"
        onChange={(event) => onChange(event.target.value)}
        className="mt-2 w-full resize-none rounded border border-[hsl(var(--border))] bg-black/40 px-3 py-2 text-sm text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] disabled:opacity-70"
      />
      <span className="mt-1 block text-[11px] text-[hsl(var(--muted-foreground))]">{value.length}/300</span>
    </label>
  );
}

function PronounsSelect({ value, onChange, disabled }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Pronouns</span>
      <select
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className="mt-2 w-full rounded border border-[hsl(var(--border))] bg-black/40 px-3 py-2 text-sm text-[hsl(var(--foreground))] disabled:opacity-70"
      >
        <option value="">Not set</option>
        <option value="He/Him">He/Him</option>
        <option value="She/Her">She/Her</option>
        <option value="They/Them">They/Them</option>
      </select>
    </label>
  );
}

function ColorPicker({ colors, value, onChange, disabled }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">
        <Palette className="w-4 h-4" /> Chat color
      </div>
      <div className="mt-3 grid grid-cols-5 gap-2">
        {colors.map((color) => {
          const selected = String(value).toLowerCase() === String(color.value).toLowerCase();
          return (
            <button
              key={color.id || color.value}
              type="button"
              disabled={disabled}
              onClick={() => onChange(color.value)}
              aria-pressed={selected}
              className={`h-10 rounded border transition ${selected ? "border-[hsl(var(--foreground))] ring-2 ring-[hsl(var(--foreground))]/40" : "border-white/20 hover:border-white/60"}`}
              style={{ background: color.value }}
              title={color.name}
            >
              <span className="sr-only">{color.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AvatarSourcePicker({ viewer, value, onChange, disabled }) {
  const hasCustom = Boolean(viewer.custom_avatar_url);
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Avatar source</div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange("platform")}
          aria-pressed={value === "platform"}
          className={`rounded border px-3 py-2 text-xs transition ${value === "platform" ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/20 text-white" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-white"}`}
        >
          Platform
        </button>
        <button
          type="button"
          disabled={disabled || !hasCustom}
          onClick={() => onChange("custom")}
          aria-pressed={value === "custom"}
          className={`rounded border px-3 py-2 text-xs transition ${value === "custom" ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/20 text-white" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-white"} ${!hasCustom ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          Custom
        </button>
      </div>
    </div>
  );
}

function TitlePicker({ titles, value, onChange, disabled }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Title</span>
      <select
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className="mt-2 w-full rounded border border-[hsl(var(--border))] bg-black/40 px-3 py-2 text-sm text-[hsl(var(--foreground))] disabled:opacity-70"
      >
        <option value="">No title</option>
        {titles.map((title) => (
          <option key={title.id} value={title.id}>{title.name}</option>
        ))}
      </select>
    </label>
  );
}

function BadgePicker({ badges, selected, onToggle, disabled }) {
  if (!badges.length) {
    return (
      <div className="rounded border border-dashed border-[hsl(var(--border))] bg-black/35 p-3 text-xs text-[hsl(var(--muted-foreground))]">
        Badges will appear here after they are awarded.
      </div>
    );
  }
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Featured badges</div>
      <div className="mt-2 flex flex-wrap gap-2">
        {badges.map((badge) => {
          const isSelected = selected.includes(badge.id);
          const lockedByLimit = !isSelected && selected.length >= 3;
          return (
            <button
              key={badge.id}
              type="button"
              disabled={disabled || lockedByLimit}
              onClick={() => onToggle(badge.id)}
              aria-pressed={isSelected}
              className={`inline-flex items-center gap-1.5 rounded border px-2.5 py-1 text-xs transition ${isSelected ? "border-transparent text-black" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-white"} ${lockedByLimit ? "opacity-50 cursor-not-allowed" : ""}`}
              style={isSelected ? { background: badge.color || "hsl(var(--foreground))" } : undefined}
            >
              <Star className="w-3.5 h-3.5" />
              {badge.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const tone = viewerRequestStatusTone(status);
  const color = tone === "success"
    ? "border-emerald-500 text-emerald-300 bg-emerald-500/10"
    : tone === "danger"
      ? "border-red-500 text-red-200 bg-red-500/10"
      : tone === "warning"
        ? "border-amber-400 text-amber-200 bg-amber-500/10"
        : "border-[hsl(var(--currency-event))] text-[hsl(var(--foreground))] bg-[hsl(var(--currency-event))]/10";
  return <span className={`inline-flex w-fit px-2.5 py-1 rounded border text-xs font-medium ${color}`}>{viewerRequestStatusLabel(status)}</span>;
}

function MiniBadge({ children }) {
  return (
    <span className="inline-flex items-center rounded bg-white/[0.05] px-2.5 py-1 text-xs text-[hsl(var(--muted-foreground))]">
      {children}
    </span>
  );
}

function ProfileTitle({ title }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded bg-white/[0.05] px-2.5 py-1 text-xs text-[hsl(var(--foreground))]">
      <Star className="w-3.5 h-3.5" style={{ color: title.color || "#F6AE2D" }} />
      {title.name}
    </span>
  );
}

function currencySourceLabel(source) {
  if (source === "admin_grant") return "Granted by streamer";
  if (source === "redeem_charge") return "Redeem charge";
  if (source === "redeem_refund") return "Redeem refund";
  if (source === "stream_online_reward") return "Stream went live reward";
  if (source === "stream_participation_reward") return "Stream participation reward";
  if (source === "twitch_event") return "Stream activity reward";
  return "Currency activity";
}

function currencyDescription(currency) {
  if (currency?.description) return currency.description;
  if (currency?.id === "free_currency") return "Earned through stream participation.";
  if (currency?.id === "paid_currency") return "Reserved for higher-value support rewards.";
  if (currency?.id === "giveaway_currency") return "Used for giveaway entries.";
  if (currency?.id === "event_currency") return "Limited-time currency for special events.";
  return "Used for community rewards and stream interactions.";
}

function normalizePronouns(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "he/him") return "He/Him";
  if (normalized === "she/her") return "She/Her";
  if (normalized === "they/them") return "They/Them";
  return "";
}

function initialsForName(name) {
  const parts = String(name || "Viewer").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "V";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function mergeAuthProfile(viewerResponse, authProfile) {
  if (!authProfile?.authenticated) {
    return viewerResponse;
  }
  const profileFields = {
    platform_user_id: authProfile.platform_user_id,
    auth_provider: authProfile.auth_provider || "",
    primary_platform: authProfile.primary_platform || "",
    preferred_name: authProfile.preferred_name || "",
    pronouns: authProfile.pronouns || "",
    role: authProfile.role || "viewer",
    profile_image_url: authProfile.profile_image_url || "",
    platform_profile_image_url: authProfile.platform_profile_image_url || "",
    custom_avatar_url: authProfile.custom_avatar_url || "",
    avatar_source: authProfile.avatar_source || "platform",
    accent_color: authProfile.accent_color || FALLBACK_ACCENT_COLORS[6].value,
    about: authProfile.about || "",
    featured_badge_ids: Array.isArray(authProfile.featured_badge_ids) ? authProfile.featured_badge_ids : [],
    featured_badges: Array.isArray(authProfile.featured_badges) ? authProfile.featured_badges : [],
    active_title_id: authProfile.active_title_id || "",
    active_title: authProfile.active_title || null,
    awards: authProfile.awards || { badges: [], titles: [], featured_badges: [], active_title: null },
    profile_options: authProfile.profile_options || { accent_colors: FALLBACK_ACCENT_COLORS },
  };
  if (!viewerResponse?.authenticated || !viewerResponse.viewer) {
    return {
      authenticated: true,
      viewer: {
        twitch_user_id: authProfile.twitch_user_id,
        login: authProfile.login,
        display_name: authProfile.display_name || authProfile.login,
        balances: {},
        watchtime_minutes: 0,
        ...profileFields,
      },
    };
  }
  return {
    ...viewerResponse,
    viewer: {
      ...viewerResponse.viewer,
      ...profileFields,
      profile_image_url: profileFields.profile_image_url || viewerResponse.viewer.profile_image_url,
    },
  };
}

function resolveMediaUrl(value) {
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith("/")) return `${BACKEND_URL}${value}`;
  return value;
}

function colorNameForValue(value, colors = FALLBACK_ACCENT_COLORS) {
  const match = (colors || FALLBACK_ACCENT_COLORS).find((color) => String(color.value).toLowerCase() === String(value || "").toLowerCase());
  return match?.name || "Profile color";
}

function platformName(platform) {
  const normalized = String(platform || "").toLowerCase();
  if (normalized === "kick") return "Kick";
  if (normalized === "twitch") return "Twitch";
  return "your viewer account";
}

function formatRole(role) {
  const normalized = String(role || "viewer").toLowerCase();
  if (normalized === "broadcaster" || normalized === "streamer") return "Streamer";
  if (normalized === "moderator" || normalized === "mod") return "Moderator";
  if (normalized === "vip") return "Vip";
  return "Viewer";
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
